/**
 * useGlobalSearchBridge - Centralized Search & Keyboard Shortcut Bridge
 * 
 * PATH: packages/ui/src/hooks/useGlobalSearchBridge.ts
 * PURPOSE: Provide unified search API integration and keyboard shortcut management
 * 
 * PROBLEM SOLVED:
 * - Previously, only CRM Hub passed `globalSearchApiClient` to DashboardLayout
 * - Other hubs had search UI but couldn't actually search
 * - Keyboard shortcuts were duplicated across hubs
 * 
 * USAGE:
 * ```tsx
 * import { useGlobalSearchBridge } from '@quotech/ui'
 * 
 * function App() {
 *   const { apiClient, shortcuts, registerShortcut } = useGlobalSearchBridge({
 *     getAccessToken: () => authStore.getState().accessToken,
 *     getOrgId: () => authStore.getState().selectedOrg?.id,
 *   })
 *   
 *   return (
 *     <DashboardLayout
 *       showGlobalSearch={true}
 *       globalSearchApiClient={apiClient}
 *       // ...
 *     />
 *   )
 * }
 * ```
 * 
 * KEYBOARD SHORTCUTS (registered by default):
 * - Cmd+K / Ctrl+K: Open global search
 * - Cmd+, / Ctrl+,: Open settings
 * - Cmd+I / Ctrl+I: Open AI sidebar
 * - Cmd+N / Ctrl+N: Quick add (if enabled)
 * - Cmd+/ / Ctrl+/: Show keyboard shortcuts help
 * 
 * NOTES FOR FUTURE AI:
 * - This hook MUST be used by all hubs that want search to work
 * - The apiClient returned is pre-configured with auth headers
 * - Shortcuts can be customized per-hub via registerShortcut
 */

import { useEffect, useCallback, useRef, useMemo } from 'react'
import axios, { type AxiosInstance } from 'axios'

// =============================================================================
// Types
// =============================================================================

export interface TokenProvider {
  getAccessToken: () => string | null
  getOrgId: () => string | number | null
}

export interface KeyboardShortcut {
  id: string
  key: string
  modifiers: ('meta' | 'ctrl' | 'alt' | 'shift')[]
  description: string
  action: () => void
  enabled?: boolean
  group?: string
}

export interface GlobalSearchBridgeConfig {
  /** Token provider for auth headers */
  tokenProvider?: TokenProvider
  /** Base URL for API requests (default: quotech-backend) */
  baseUrl?: string
  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean
  /** Custom shortcuts to register */
  customShortcuts?: KeyboardShortcut[]
  /** Callback when search opens */
  onSearchOpen?: () => void
  /** Callback when settings opens */
  onSettingsOpen?: () => void
  /** Callback when AI sidebar opens */
  onAIOpen?: () => void
}

export interface GlobalSearchApiClient {
  post: (url: string, data: any) => Promise<any>
  get: (url: string) => Promise<any>
}

export interface GlobalSearchBridgeReturn {
  /** Pre-configured API client for GlobalSearch component */
  apiClient: GlobalSearchApiClient
  /** All registered keyboard shortcuts */
  shortcuts: KeyboardShortcut[]
  /** Register a new shortcut */
  registerShortcut: (shortcut: KeyboardShortcut) => void
  /** Unregister a shortcut by ID */
  unregisterShortcut: (id: string) => void
  /** Check if search is available */
  isSearchAvailable: boolean
}

// =============================================================================
// Default Shortcuts
// =============================================================================

const DEFAULT_SHORTCUTS: Omit<KeyboardShortcut, 'action'>[] = [
  {
    id: 'global-search',
    key: 'k',
    modifiers: ['meta'],
    description: 'Open search',
    group: 'Navigation',
  },
  {
    id: 'settings',
    key: ',',
    modifiers: ['meta'],
    description: 'Open settings',
    group: 'Navigation',
  },
  {
    id: 'ai-sidebar',
    key: 'i',
    modifiers: ['meta'],
    description: 'Toggle AI sidebar',
    group: 'AI',
  },
  {
    id: 'shortcuts-help',
    key: '/',
    modifiers: ['meta'],
    description: 'Show keyboard shortcuts',
    group: 'Help',
  },
]

// =============================================================================
// Hook Implementation
// =============================================================================

export function useGlobalSearchBridge(
  config: GlobalSearchBridgeConfig = {}
): GlobalSearchBridgeReturn {
  const {
    tokenProvider,
    baseUrl = import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai',
    enableShortcuts = true,
    customShortcuts = [],
    onSearchOpen,
    onSettingsOpen,
    onAIOpen,
  } = config

  const shortcutsRef = useRef<Map<string, KeyboardShortcut>>(new Map())
  
  // Create API client with auth headers
  const apiClient = useMemo<GlobalSearchApiClient>(() => {
    const axiosInstance = axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add auth interceptor
    axiosInstance.interceptors.request.use((config) => {
      if (tokenProvider) {
        const token = tokenProvider.getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        const orgId = tokenProvider.getOrgId()
        if (orgId) {
          config.headers['X-Organisation-ID'] = String(orgId)
        }
      }
      return config
    })

    // Wrap to match expected interface
    return {
      post: async (url: string, data: any) => {
        const response = await axiosInstance.post(url, data)
        return response
      },
      get: async (url: string) => {
        const response = await axiosInstance.get(url)
        return response
      },
    }
  }, [baseUrl, tokenProvider])

  // Register shortcut
  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    shortcutsRef.current.set(shortcut.id, shortcut)
  }, [])

  // Unregister shortcut
  const unregisterShortcut = useCallback((id: string) => {
    shortcutsRef.current.delete(id)
  }, [])

  // Initialize default shortcuts with provided callbacks
  useEffect(() => {
    // Clear existing defaults
    DEFAULT_SHORTCUTS.forEach(s => shortcutsRef.current.delete(s.id))

    // Register defaults with actions
    if (onSearchOpen) {
      registerShortcut({
        ...DEFAULT_SHORTCUTS.find(s => s.id === 'global-search')!,
        action: onSearchOpen,
      })
    }

    if (onSettingsOpen) {
      registerShortcut({
        ...DEFAULT_SHORTCUTS.find(s => s.id === 'settings')!,
        action: onSettingsOpen,
      })
    }

    if (onAIOpen) {
      registerShortcut({
        ...DEFAULT_SHORTCUTS.find(s => s.id === 'ai-sidebar')!,
        action: onAIOpen,
      })
    }

    // Register custom shortcuts
    customShortcuts.forEach(registerShortcut)
  }, [onSearchOpen, onSettingsOpen, onAIOpen, customShortcuts, registerShortcut])

  // Keyboard event handler
  useEffect(() => {
    if (!enableShortcuts) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if in input/textarea
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Allow Cmd+K even in inputs (common UX pattern)
        if (!(e.key === 'k' && (e.metaKey || e.ctrlKey))) {
          return
        }
      }

      // Check each registered shortcut
      for (const shortcut of shortcutsRef.current.values()) {
        if (shortcut.enabled === false) continue

        const keyMatches = e.key.toLowerCase() === shortcut.key.toLowerCase()
        const modifiersMatch = shortcut.modifiers.every(mod => {
          if (mod === 'meta') return e.metaKey || e.ctrlKey // Allow Ctrl as Meta on Windows
          if (mod === 'ctrl') return e.ctrlKey
          if (mod === 'alt') return e.altKey
          if (mod === 'shift') return e.shiftKey
          return false
        })

        if (keyMatches && modifiersMatch) {
          e.preventDefault()
          shortcut.action()
          return
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enableShortcuts])

  // Get all shortcuts as array
  const shortcuts = useMemo(() => {
    return Array.from(shortcutsRef.current.values())
  }, [shortcutsRef.current.size])

  return {
    apiClient,
    shortcuts,
    registerShortcut,
    unregisterShortcut,
    isSearchAvailable: !!tokenProvider,
  }
}

// =============================================================================
// Utility: Format Shortcut Display
// =============================================================================

export function formatShortcut(shortcut: KeyboardShortcut): string {
  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac')
  
  const modifierSymbols: Record<string, string> = {
    meta: isMac ? '⌘' : 'Ctrl',
    ctrl: isMac ? '⌃' : 'Ctrl',
    alt: isMac ? '⌥' : 'Alt',
    shift: '⇧',
  }

  const parts = shortcut.modifiers.map(m => modifierSymbols[m])
  parts.push(shortcut.key.toUpperCase())
  
  return parts.join(isMac ? '' : '+')
}

// =============================================================================
// Utility: Create Hub-Specific Search Config
// =============================================================================

export interface HubSearchConfig {
  hubId: string
  hubName: string
  quickActions?: Array<{
    id: string
    label: string
    shortcut?: string
    icon: string
    url?: string
    action?: () => void
  }>
  quickNavItems?: Array<{
    id: string
    label: string
    description?: string
    icon: any
    href: string
    color: string
  }>
}

export function createHubSearchConfig(config: HubSearchConfig) {
  return config
}

export default useGlobalSearchBridge
